/**
 * BookshelfPage - Main container for Virtual Bookshelf feature
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import AppHeader from '@/components/home/AppHeader';
import { CURRENT_USER } from '@/data';
import { bookHref } from '@/lib/bookModel';
import { useBookshelf } from './context/BookshelfContext';
import { useModalState } from './hooks';
import { BOOKSHELF } from '@/constants/testIds';
import ReadingCorner from './ReadingCorner';
import ShelfNavigation from './ShelfNavigation';
import FilterSortBar from './FilterSortBar';
import BookGrid from './BookGrid';
import AddBookModal from './modals/AddBookModal';
import CreateShelfModal from './modals/CreateShelfModal';

const SHELF_TO_EMPTY_TYPE = {
  shelf_all: 'all',
  shelf_tbr: 'tbr',
  shelf_reading: 'reading',
  shelf_finished: 'finished',
  shelf_favorites: 'favorites',
};

export default function BookshelfPage() {
  const {
    shelves,
    filteredBooks,
    filters,
    setFilters,
    stats,
    activeShelf,
    setActiveShelf,
    loading,
    error,
    clearError,
  } = useBookshelf();

  const [isAddBookOpen, openAddBook, closeAddBook] = useModalState(false);
  const [isCreateShelfOpen, openCreateShelf, closeCreateShelf] = useModalState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Deep-linking support, e.g. the profile menu's "TBR" shortcut
  // (/app/bookshelf?shelf=shelf_tbr) should land straight on that shelf.
  React.useEffect(() => {
    const shelfParam = searchParams.get('shelf');
    if (shelfParam) setActiveShelf(shelfParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBookOpen = (book) => {
    navigate(bookHref(book.id));
  };

  const activeShelfObj = shelves.find(s => s.id === activeShelf);
  const isCustomShelf = activeShelfObj?.type === 'custom';
  const hasSearch = Boolean(filters.searchQuery?.trim());
  const hasActiveFilters = filters.genres.length > 0 || filters.status.length > 0 || filters.rating !== null || filters.isFavoriteOnly;

  let emptyStateType = SHELF_TO_EMPTY_TYPE[activeShelf] ?? (isCustomShelf ? 'custom' : 'all');
  let emptyStateOverrides;
  if (hasSearch) {
    emptyStateType = 'search';
  } else if (hasActiveFilters) {
    emptyStateType = 'filter';
  } else if (isCustomShelf) {
    emptyStateOverrides = { heading: `${activeShelfObj.name} is empty.` };
  }

  const handleEmptyCTA = () => {
    if (hasSearch) {
      setFilters(prev => ({ ...prev, searchQuery: '' }));
    } else if (hasActiveFilters) {
      setFilters(prev => ({ ...prev, genres: [], status: [], rating: null, isFavoriteOnly: false }));
    } else {
      openAddBook();
    }
  };

  return (
    <div className="relative min-h-screen bg-cream" data-testid={BOOKSHELF.page}>
      <AppHeader />

      <main className="pb-28 md:pb-16">
        {error && (
          <div className="u-container mt-6">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-coral/30 bg-coral/10 px-4 py-3 text-sm text-coral">
              <span>{error}</span>
              <button onClick={clearError} aria-label="Dismiss error" className="rounded-full p-1 hover:bg-coral/10">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <ReadingCorner
          stats={stats}
          activeShelf={activeShelf}
          onStatClick={setActiveShelf}
          onAddBookClick={openAddBook}
          userName={CURRENT_USER?.name}
        />

        <ShelfNavigation
          shelves={shelves}
          activeShelf={activeShelf}
          onShelfChange={setActiveShelf}
          onCreateShelfClick={openCreateShelf}
        />

        <FilterSortBar />

        <BookGrid
          books={filteredBooks}
          isLoading={loading}
          isEmpty={filteredBooks.length === 0}
          emptyStateType={emptyStateType}
          emptyStateOverrides={emptyStateOverrides}
          onEmptyCTAClick={handleEmptyCTA}
          onBookOpen={handleBookOpen}
        />
      </main>

      <button
        type="button"
        onClick={openAddBook}
        aria-label="Add Book"
        className="fixed bottom-20 right-6 z-30 grid h-14 w-14 place-items-center rounded-full bg-coral-fill text-[#FFF7F0] shadow-lift transition-transform hover:-translate-y-0.5 md:hidden"
      >
        <Plus className="h-6 w-6" />
      </button>

      <AddBookModal isOpen={isAddBookOpen} onClose={closeAddBook} />
      <CreateShelfModal isOpen={isCreateShelfOpen} onClose={closeCreateShelf} onCreated={(shelf) => setActiveShelf(shelf.id)} />
    </div>
  );
}
